import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import TitleBanner from "./TitleBanner.jsx";
import UnderwaterBackground from "./UnderwaterBackground.jsx";
import PublishWheelSnapshot from "./PublishWheelSnapshot.jsx";

function PublishWindow({
  visible,
  onClose,
  bannerTitle,
  bannerSubtitle,
  leftGif,
  rightGif,
  publishWheelSnapshot,
  getNameIconSrc,
  showQuagganEmojis,
  winnerSelector,
}) {
  const winnerMountRef = useRef(null);
  const [winnerSourceNode, setWinnerSourceNode] = useState(null);

  const cloneForPublish = (sourceNode, kind) => {
    if (!sourceNode) return null;

    const clone = sourceNode.cloneNode(true);

    if (kind === "winner") {
      clone.querySelectorAll(".publish-button-container").forEach((publishButtonNode) => {
        publishButtonNode.remove();
      });

      clone.querySelectorAll("button").forEach((buttonNode) => {
        buttonNode.remove();
      });

      clone.style.width = "100%";
      clone.style.maxWidth = "100%";
      clone.style.minHeight = "100%";
      clone.style.maxHeight = "none";
      clone.style.height = "100%";
      clone.style.padding = "20px 20px 0 20px";
      clone.style.transform = "none";
      clone.style.margin = "0";
      clone.style.backdropFilter = "none";

      clone.querySelectorAll("*").forEach((node) => {
        if (node.style && node.style.backdropFilter) {
          node.style.backdropFilter = "none";
        }
      });
    }

    return clone;
  };

  const clearState = () => {
    setWinnerSourceNode(null);
  };

  useEffect(() => {
    if (!visible) {
      document.body.style.overflowX = "";
      document.body.style.overflowY = "";
      clearState();
      return undefined;
    }

    document.body.style.overflowX = "hidden";
    document.body.style.overflowY = "hidden";

    let cancelled = false;

    const locateAssets = async () => {
      const winnerNode = document.querySelector(winnerSelector);

      if (!winnerNode) {
        return;
      }

      try {
        if (cancelled) return;

        setWinnerSourceNode(winnerNode);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to prepare publish preview:", error);
        }
      }
    };

    const timer = window.setTimeout(() => {
      locateAssets();
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.body.style.overflowX = "";
      document.body.style.overflowY = "";
    };
  }, [visible, winnerSelector]);

  useEffect(() => {
    const mountNode = winnerMountRef.current;
    if (!mountNode) return;

    mountNode.innerHTML = "";
    const clonedWinner = cloneForPublish(winnerSourceNode, "winner");
    if (!clonedWinner) return;

    mountNode.appendChild(clonedWinner);

    const fitWinnerPreview = () => {
      const availableHeight = mountNode.clientHeight;
      const winnerHeight = clonedWinner.getBoundingClientRect().height;
      if (!availableHeight || !winnerHeight) return;

      const scale = Math.min(1.2, availableHeight / winnerHeight);
      clonedWinner.style.transform = `scale(${scale})`;
      clonedWinner.style.transformOrigin = "top center";
      clonedWinner.style.width = `${100 / scale}%`;
    };

    const rafId = window.requestAnimationFrame(fitWinnerPreview);
    window.addEventListener("resize", fitWinnerPreview);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", fitWinnerPreview);
    };
  }, [winnerSourceNode]);

  if (!visible) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(2, 10, 28, 0.72)",
        backdropFilter: "blur(6px)",
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "auto",
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          style={{
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            boxSizing: "border-box",
            position: "relative",
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <div
            style={{
              position: "relative",
              width: "min(1180px, 92vw)",
              height: "min(860px, calc(100vh - 52px))",
            }}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
          >
            <div
              data-publish-capture-root="true"
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: 14,
                boxSizing: "border-box",
                border: "1px solid rgba(120, 220, 255, 0.2)",
                borderRadius: 28,
                background:
                  "linear-gradient(180deg, rgba(8, 27, 54, 0.98) 0%, rgba(3, 12, 28, 0.99) 100%)",
                boxShadow: "0 18px 60px rgba(0, 0, 0, 0.42)",
                overflow: "hidden",
              }}
            >
              <UnderwaterBackground staticMode />

              <div style={{ display: "flex", justifyContent: "center", position: "relative", zIndex: 1 }}>
                <TitleBanner
                  title={bannerTitle}
                  subtitle={bannerSubtitle}
                  leftGif={leftGif}
                  rightGif={rightGif}
                  bobbing={false}
                />
              </div>

              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1.08fr) minmax(0, 0.92fr)",
                  gap: 12,
                  alignItems: "stretch",
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    border: "1px solid rgba(120, 220, 255, 0.2)",
                    borderRadius: 28,
                    background:
                      "radial-gradient(circle at 50% 20%, rgba(64, 200, 255, 0.14), rgba(3, 14, 32, 0.96) 60%)",
                    boxShadow: "0 18px 60px rgba(0, 0, 0, 0.42)",
                    padding: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    <PublishWheelSnapshot
                      snapshot={publishWheelSnapshot}
                      getNameIconSrc={getNameIconSrc}
                      showQuagganEmojis={showQuagganEmojis}
                    />
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid rgba(120, 220, 255, 0.2)",
                    borderRadius: 28,
                    background:
                      "radial-gradient(circle at 50% 20%, rgba(64, 200, 255, 0.12), rgba(3, 14, 32, 0.96) 60%)",
                    boxShadow: "0 18px 60px rgba(0, 0, 0, 0.42)",
                    padding: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {winnerSourceNode ? (
                    <div
                      ref={winnerMountRef}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        color: "rgba(190, 230, 255, 0.8)",
                        fontSize: 14,
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                      }}
                    >
                      Preparing winner menu preview...
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="publish-window-close-button"
              onClick={onClose}
              aria-label="Close publish window"
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                width: 40,
                height: 40,
                borderRadius: 999,
                border: "1px solid rgba(120, 220, 255, 0.35)",
                background: "rgba(4, 16, 34, 0.9)",
                color: "#d8f4ff",
                cursor: "pointer",
                boxShadow: "0 8px 20px rgba(0, 0, 0, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
                zIndex: 2,
              }}
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default PublishWindow;
